import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mockOrders } from "@/mocks/orders";
import { mockWishlist } from "@/mocks/wishlist";

interface AccountLayoutProps {
  children: React.ReactNode;
  activeTab: "dashboard" | "orders" | "wishlist" | "settings";
}

export default function AccountLayout({ children, activeTab }: AccountLayoutProps) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { user } = useAuth();

  const orderCount = mockOrders.filter((o) => o.status !== "cancelled").length;
  const wishlistCount = mockWishlist.length;

  const navItems = [
    {
      id: "dashboard" as const,
      href: "/account",
      label: t("account.dashboard"),
      icon: "ri-dashboard-line",
      badge: null,
    },
    {
      id: "orders" as const,
      href: "/account/orders",
      label: t("account.orders"),
      icon: "ri-shopping-bag-line",
      badge: orderCount,
    },
    {
      id: "wishlist" as const,
      href: "/account/wishlist",
      label: t("account.wishlist"),
      icon: "ri-heart-line",
      badge: wishlistCount,
    },
    {
      id: "settings" as const,
      href: "/account/settings",
      label: t("account.settings"),
      icon: "ri-settings-3-line",
      badge: null,
    },
  ];

  return (
    <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Page header */}
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {t("account.my_account")}
              </h1>
              {user && (
                <p className="text-sm text-foreground-500 mt-1">
                  {t("account.welcome_back")}, {user.full_name}
                </p>
              )}
            </div>

            <div className={`flex gap-8 ${isAr ? "flex-row-reverse" : ""}`}>
              {/* Sidebar */}
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <nav className="sticky top-24 space-y-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.id}
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                        activeTab === item.id
                          ? "bg-primary-50 text-primary-700"
                          : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          activeTab === item.id
                            ? "bg-primary-100"
                            : "bg-background-100"
                        }`}
                      >
                        <i className={`${item.icon} text-base`}></i>
                      </div>
                      <span className="flex-1">{item.label}</span>
                      {item.badge !== null && item.badge > 0 && (
                        <span className="px-2 py-0.5 bg-background-200 text-foreground-600 text-xs font-medium rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </nav>
              </aside>

              {/* Mobile tab nav */}
              <div className="lg:hidden w-full mb-6 overflow-x-auto">
                <div className="flex gap-2 min-w-max pb-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.id}
                      to={item.href}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        activeTab === item.id
                          ? "bg-primary-500 text-white"
                          : "text-foreground-600 bg-background-100 hover:bg-background-200"
                      }`}
                    >
                      <i className={item.icon}></i>
                      {item.label}
                      {item.badge !== null && item.badge > 0 && (
                        <span
                          className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                            activeTab === item.id
                              ? "bg-white/20 text-white"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">{children}</div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}